/**
 * OWL2 Property Expression Types
 *
 * Defines object property expressions and data property expressions.
 * Object property expressions can be either a named property or an inverse.
 * Data property expressions are always named properties.
 */
// =============================================================================
// Factory Functions
// =============================================================================
/**
 * Create a named object property.
 */
export function objectProperty(iri) {
    return { type: 'ObjectProperty', iri };
}
/**
 * Create an inverse object property expression.
 */
export function objectInverseOf(property) {
    return { type: 'ObjectInverseOf', property };
}
/**
 * Create a named data property.
 */
export function dataProperty(iri) {
    return { type: 'DataProperty', iri };
}
/**
 * Create an object property chain.
 * Requires at least 2 properties.
 */
export function objectPropertyChain(first, second, ...rest) {
    return {
        type: 'ObjectPropertyChain',
        properties: [first, second, ...rest]
    };
}
// =============================================================================
// Type Guards
// =============================================================================
/**
 * Check if an object property expression is a named property.
 */
export function isObjectProperty(expr) {
    return expr.type === 'ObjectProperty';
}
/**
 * Check if an object property expression is an inverse.
 */
export function isObjectInverseOf(expr) {
    return expr.type === 'ObjectInverseOf';
}
/**
 * Check if a sub-property expression is a property chain.
 */
export function isObjectPropertyChain(expr) {
    return expr.type === 'ObjectPropertyChain';
}
/**
 * Check if a sub-property expression is a single property expression.
 */
export function isObjectPropertyExpression(expr) {
    return expr.type === 'ObjectProperty' || expr.type === 'ObjectInverseOf';
}
// =============================================================================
// Utility Functions
// =============================================================================
/**
 * Get the IRI of a property expression.
 * For ObjectInverseOf, returns the IRI of the wrapped property.
 */
export function getPropertyIRI(expr) {
    switch (expr.type) {
        case 'ObjectProperty':
        case 'DataProperty':
            return expr.iri;
        case 'ObjectInverseOf':
            return expr.property.iri;
    }
}
/**
 * Get all property IRIs from a sub-property expression.
 * For property chains, returns all IRIs in the chain.
 */
export function getAllPropertyIRIs(expr) {
    if (isObjectPropertyChain(expr)) {
        return expr.properties.map(getPropertyIRI);
    }
    return [getPropertyIRI(expr)];
}
